import { EditorView } from 'prosemirror-view';
import { ChangeEvent, useState } from 'react';
import { findPlaceholder, placeholderPlugin } from '../placeholderPlugin';

export const useEditorImageHandlers = (
  view: Pick<EditorView, 'state' | 'dispatch'> | null | undefined,
) => {
  const maxImageWidth = 600;
  const maxImageHeight = 2000;
  const [imagesScaledDown, setImagesScaledDown] = useState(false);
  const scaleImageFromURLAndConvertToBase64 = (
    url: string,
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = url;
      image.onload = () => {
        if (image.width <= maxImageWidth && url.startsWith('data:')) {
          resolve(url);
          return;
        }
        const useJpeg = url.includes('jpg') || url.includes('jpeg');
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const scaleSize = Math.min(
            1,
            maxImageWidth / image.width,
            maxImageHeight / image.height,
          );
          canvas.width = Math.min(image.width, maxImageWidth);
          canvas.height = image.height * scaleSize;
          ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);
          if (url.startsWith('data:')) {
            setImagesScaledDown(true);
          }
          resolve(canvas.toDataURL(useJpeg ? 'image/jpeg' : 'image/png'));
        } catch (e) {
          reject();
          throw e;
        }
      };
      image.onerror = () => {
        reject();
      };
    });
  };

  const resizeAndAddImage = async (
    urlPromise: Promise<string>,
    pos?: number | undefined,
  ) => {
    const id = {};
    const tr = view?.state.tr;
    if (!tr) {
      return;
    }
    if (pos === undefined && !tr.selection.empty) {
      tr.deleteSelection();
    }
    tr.setMeta(placeholderPlugin, {
      add: { id, pos: pos === undefined ? tr.selection.from : pos },
    });
    view.dispatch(tr);

    try {
      const resizedUrl = await scaleImageFromURLAndConvertToBase64(
        await urlPromise,
      );

      const currentPos = findPlaceholder(view.state, id);

      if (currentPos === null) {
        // Someone erased the placeholder
        return;
      }

      view.dispatch(
        view.state.tr
          .replaceWith(
            currentPos,
            currentPos,
            view.state.schema.nodes.image.create({ src: resizedUrl }),
          )
          .setMeta(placeholderPlugin, { remove: { id } }),
      );
    } catch {
      view.dispatch(tr.setMeta(placeholderPlugin, { remove: { id } }));
    }
  };
  const handleDataTransfer = (
    dataTransfer: DataTransfer,
    pos?: number | undefined,
  ) => {
    const textData = dataTransfer.getData('text/html');
    const files = Array.from(dataTransfer?.files || []).filter(file =>
      file.type.startsWith('image/'),
    );

    if (textData) {
      const container = document.createElement('div');
      container.innerHTML = textData;
      const firstChild = container.children[0];
      if (
        // The dragging comes from ProseMirror, in that case let ProseMirror handle the event.
        !(firstChild instanceof HTMLElement) ||
        firstChild.dataset.pmSlice
      ) {
        return false;
      }
      const interestingChildren = Array.from(container.children).filter(
        child => !(child instanceof HTMLMetaElement),
      );
      if (
        interestingChildren.length === 1 &&
        interestingChildren[0] instanceof HTMLImageElement &&
        !interestingChildren[0].dataset.pmSlice
      ) {
        resizeAndAddImage(Promise.resolve(interestingChildren[0].src), pos);
        return true;
      }
      return false;
    }

    if (files.length) {
      for (const file of files) {
        const urlPromise = new Promise<string>(resolve => {
          const reader = new FileReader();
          reader.onload = async () => {
            resolve(reader.result as string);
          };
          reader.readAsDataURL(file);
        });
        resizeAndAddImage(urlPromise, pos);
      }

      return true;
    }
    return false;
  };
  const handlePaste = (view: EditorView, event: ClipboardEvent) => {
    if (!event.clipboardData) {
      return false;
    }
    if (handleDataTransfer(event.clipboardData)) {
      event.stopPropagation();
      event.preventDefault();
      return true;
    }
    return false;
  };
  const handleDrop = (view: EditorView, event: DragEvent) => {
    const pos = view.posAtCoords({
      left: event.clientX,
      top: event.clientY,
    })?.pos;
    if (!pos || !event.dataTransfer) {
      return false;
    }
    if (handleDataTransfer(event.dataTransfer, pos)) {
      event.stopPropagation();
      event.preventDefault();
      return true;
    }
    return false;
  };
  const onImageSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    if (
      view?.state.selection.$from.parent.inlineContent &&
      e.target.files?.length
    ) {
      const imageFiles = Array.from(e.target.files).filter(file =>
        file.type.startsWith('image/'),
      );
      e.target.value = '';

      if (imageFiles.length) {
        const { pos } = view.state.selection.$from;
        for (const file of imageFiles) {
          const urlPromise = new Promise<string>(resolve => {
            const reader = new FileReader();
            reader.onload = async () => {
              resolve(reader.result as string);
            };
            reader.readAsDataURL(file);
          });
          // eslint-disable-next-line no-await-in-loop
          await resizeAndAddImage(urlPromise, pos);
        }
      }
    }
  };

  return {
    handlePaste,
    handleDrop,
    onImageSelected,
    maxImageWidth,
    maxImageHeight,
    imagesScaledDown,
  };
};
